import { UseFormReturn } from 'react-hook-form';
import {
    FormInputText,
    FormSelector,
    FormTextEditor,
    Grid,
    Icon,
    IconButton,
    useTheme,
    Typography,
    useAppSelector,
    IOption,
    useAppDispatch,
} from '../../../../base';
import { SAVE_JOB_FORM_VALUES, ISaveJobForm } from '../../../../common';
import { useTranslation } from 'react-i18next';
import { jobsActions, jobsSelectors, userSelectors } from '../../../../actions';
import { useCallback, useEffect, useRef, useState } from 'react';
import PargodaLoading from '../../../PargodaLoading';
import {
    TextEditorWrapper,
    LoadingOverlay,
} from './JobDescriptionAndAttributes.styles';

interface Props {
    methods: UseFormReturn<ISaveJobForm>;
}

const JobDescriptionAndAttributes = (props: Props) => {
    const { methods } = props;
    const { watch, setValue, getValues, control } = methods;
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const [isOverviewClicked, setIsOverviewClicked] = useState(false);
    const [isResponsibilitiesClicked, setIsResponsibilitiesClicked] =
        useState(false);
    const previousJobTitleRef = useRef<string>('');

    const jobAttributeList = useAppSelector(jobsSelectors.getJobAttributeList);
    const companyUsersOptions = useAppSelector(userSelectors.getUsersOptions);
    const jobOverviewAndResponsibilitiesRecommmendation = useAppSelector(
        jobsSelectors.getJobOverviewAndResponsibilities
    );
    const isJobOverviewAndResponsibilitiesRecommmendationLoading =
        useAppSelector(
            jobsSelectors.getIsJobOverviewAndResponsibilitiesLoading
        );
    const jobTitle = watch(SAVE_JOB_FORM_VALUES.TITLE);

    useEffect(() => {
        return () => {
            setIsOverviewClicked(false);
            setIsResponsibilitiesClicked(false);
            dispatch(
                jobsActions.setJobOverviewAndResponsibilities({
                    overviewAndResponsibilities: {
                        overview: null,
                        responsibilities: null,
                    },
                })
            );
        };
    }, [dispatch]);

    useEffect(() => {
        if (jobTitle && jobTitle !== previousJobTitleRef.current) {
            if (
                jobOverviewAndResponsibilitiesRecommmendation?.overview !==
                    null ||
                jobOverviewAndResponsibilitiesRecommmendation?.responsibilities !==
                    null
            ) {
                dispatch(
                    jobsActions.setJobOverviewAndResponsibilities({
                        overviewAndResponsibilities: {
                            overview: null,
                            responsibilities: null,
                        },
                    })
                );
            }
            previousJobTitleRef.current = jobTitle;
        }
    }, [jobTitle, jobOverviewAndResponsibilitiesRecommmendation, dispatch]);

    const setJobOverviewAndResponsibilitiesValues = useCallback(() => {
        if (isOverviewClicked) {
            setValue(
                SAVE_JOB_FORM_VALUES.JOB_OVERVIEW,
                jobOverviewAndResponsibilitiesRecommmendation?.overview || '',
                { shouldDirty: true }
            );
            setIsOverviewClicked(false);
        }
        if (isResponsibilitiesClicked) {
            setValue(
                SAVE_JOB_FORM_VALUES.RESPONSIBILITIES,
                jobOverviewAndResponsibilitiesRecommmendation?.responsibilities ||
                    '',
                { shouldDirty: true }
            );
            setIsResponsibilitiesClicked(false);
        }
    }, [
        isOverviewClicked,
        isResponsibilitiesClicked,
        jobOverviewAndResponsibilitiesRecommmendation,
        setValue,
    ]);

    useEffect(() => {
        if (isJobOverviewAndResponsibilitiesRecommmendationLoading) {
            return;
        }
        setJobOverviewAndResponsibilitiesValues();
    }, [
        isOverviewClicked,
        isResponsibilitiesClicked,
        jobOverviewAndResponsibilitiesRecommmendation,
        isJobOverviewAndResponsibilitiesRecommmendationLoading,
        setJobOverviewAndResponsibilitiesValues,
    ]);

    const theme = useTheme();

    const onAIButtonClick = () => {
        if (
            jobOverviewAndResponsibilitiesRecommmendation?.overview !== null ||
            jobOverviewAndResponsibilitiesRecommmendation?.responsibilities !==
                null
        ) {
            setJobOverviewAndResponsibilitiesValues();
        } else {
            const formData = getValues();
            const payload = {
                title: formData[SAVE_JOB_FORM_VALUES.TITLE],
                company_overview:
                    formData[SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW] || '',
                job_attributes: formData[SAVE_JOB_FORM_VALUES.ATTRIBUTE]
                    ?.filter((attribute: { value: IOption | null }) => {
                        return attribute?.value?.value;
                    })
                    .map(
                        (attribute: { value: IOption | null }) =>
                            attribute?.value?.value
                    ),
            };
            dispatch(jobsActions.fetchJobOverviewAndResponsibilities(payload));
        }
    };

    return (
        <Grid container gap={1.5} mt={5}>
            <Grid container item xs={12} gap={1.5}>
                <Grid item width={412}>
                    <FormInputText
                        name={SAVE_JOB_FORM_VALUES.TITLE}
                        control={control}
                        label={t('saveJob.jobTitle')}
                        required
                        inputProps={{ maxLength: 120 }}
                        allowMoreCharactersThanMax
                    />
                </Grid>
                <Grid item width={200}>
                    <FormInputText
                        name={SAVE_JOB_FORM_VALUES.LOCATION}
                        control={control}
                        label={t('saveJob.location')}
                        inputProps={{ maxLength: 64 }}
                        allowMoreCharactersThanMax
                    />
                </Grid>
            </Grid>
            <Grid container item xs={12} gap={1.5}>
                <Grid item width={200}>
                    <FormInputText
                        name={SAVE_JOB_FORM_VALUES.EXTERNAL_JOB_ID}
                        control={control}
                        label={t('saveJob.externalJobId')}
                        inputProps={{ maxLength: 64 }}
                        allowMoreCharactersThanMax
                    />
                </Grid>
                {jobAttributeList?.map((attribute, index) => (
                    <Grid key={attribute.id} item width={200}>
                        <FormSelector
                            name={`${SAVE_JOB_FORM_VALUES.ATTRIBUTE}.${index}.value`}
                            control={control}
                            options={attribute.values.map((value) => ({
                                label: value.name,
                                value: value.id,
                            }))}
                            label={attribute.name}
                        />
                    </Grid>
                ))}
                <Grid item width={200}>
                    <FormSelector
                        name={SAVE_JOB_FORM_VALUES.RECRUITING_MANAGER}
                        control={control}
                        options={companyUsersOptions}
                        label={t('saveJob.recruitingManager')}
                    />
                </Grid>
                <Grid item width={200}>
                    <FormSelector
                        name={SAVE_JOB_FORM_VALUES.RECRUITER}
                        control={control}
                        options={companyUsersOptions}
                        label={t('saveJob.recruiter')}
                        disableClearable
                    />
                </Grid>
            </Grid>
            <Grid container item xs={12} columnSpacing={3}>
                <Grid item xs={4}>
                    <Typography variant="h5">
                        {t('saveJob.companyOverview')}
                    </Typography>
                    <FormTextEditor
                        name={SAVE_JOB_FORM_VALUES.COMPANY_OVERVIEW}
                        control={control}
                        maxCharacterCount={1024}
                    />
                </Grid>
                <Grid item xs={4}>
                    <Grid container display="flex" alignItems="center" gap={1}>
                        <Typography variant="h5">
                            {t('saveJob.jobOverview')}
                        </Typography>
                        <IconButton
                            onClick={() => {
                                methods.trigger(SAVE_JOB_FORM_VALUES.TITLE);
                                if (
                                    methods.formState.errors[
                                        SAVE_JOB_FORM_VALUES.TITLE
                                    ]
                                ) {
                                    return;
                                }
                                setIsOverviewClicked(true);
                                onAIButtonClick();
                            }}
                            sx={{
                                height: '22px',
                                width: '22px',
                                padding: 0,
                            }}
                        >
                            <Icon
                                name="AIGenerateTextIcon"
                                size={18}
                                color={theme.palette.secondary.main}
                            />
                        </IconButton>
                    </Grid>
                    <TextEditorWrapper>
                        <FormTextEditor
                            name={SAVE_JOB_FORM_VALUES.JOB_OVERVIEW}
                            control={control}
                            maxCharacterCount={1024}
                        />
                        {isOverviewClicked && (
                            <LoadingOverlay>
                                <PargodaLoading height={300} width={300} />
                            </LoadingOverlay>
                        )}
                    </TextEditorWrapper>
                </Grid>
                <Grid item xs={4}>
                    <Grid container display="flex" alignItems="center" gap={1}>
                        <Typography variant="h5">
                            {t('saveJob.responsibilities')}
                        </Typography>
                        <IconButton
                            onClick={() => {
                                methods.trigger(SAVE_JOB_FORM_VALUES.TITLE);
                                if (
                                    methods.formState.errors[
                                        SAVE_JOB_FORM_VALUES.TITLE
                                    ]
                                ) {
                                    return;
                                }
                                onAIButtonClick();
                                setIsResponsibilitiesClicked(true);
                            }}
                            sx={{
                                height: '22px',
                                width: '22px',
                                padding: 0,
                            }}
                        >
                            <Icon
                                name="AIGenerateTextIcon"
                                size={18}
                                color={theme.palette.secondary.main}
                            />
                        </IconButton>
                    </Grid>
                    <TextEditorWrapper>
                        <FormTextEditor
                            name={SAVE_JOB_FORM_VALUES.RESPONSIBILITIES}
                            control={control}
                            maxCharacterCount={1024}
                            disabled={isResponsibilitiesClicked}
                        />
                        {isResponsibilitiesClicked && (
                            <LoadingOverlay>
                                <PargodaLoading height={300} width={300} />
                            </LoadingOverlay>
                        )}
                    </TextEditorWrapper>
                </Grid>
            </Grid>
        </Grid>
    );
};

export default JobDescriptionAndAttributes;
